import { faBuildingMagnifyingGlass, faCodeCompare, faSlidersUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap-5';
import { getComps } from 'bundles/connect/helpers/prospect_api';
import { Button } from 'components/ui/button';
import OffMarketComps from '../search/components/off_market_comps';
import OffMarketDetailsCard from '../search/components/off_market_details_card';
import EmptyState from '../empty_state';
import type { InsightsProperty } from 'bundles/connect/types';

type Props = {
  fromTurbo?: boolean;
  hasAccess: boolean;
  subjectProperty: InsightsProperty;
}

const COMPS_DISTANCE_OPTIONS_FOR_SELECT = [
  ['0.1 Miles', 0.1],
  ['0.25 Miles', 0.25],
  ['0.5 Miles', 0.5],
  ['1 Mile', 1],
  ['1.5 Miles', 1.5],
  ['2 Miles', 2],
  ['5 Miles', 5]
];

const COMPS_TIME_PERIOD_OPTIONS_FOR_SELECT = [
  ['3 Months', 3],
  ['6 Months', 6],
  ['12 Months', 12],
  ['2 Years', 24],
  ['3 Years', 36],
  ['5 Years', 60]
];

const COMPS_PRICE_RANGE_OPTIONS_FOR_SELECT = [
  ["Don't Apply", ''],
  ['+/- 10%', 0.1],
  ['+/- 20%', 0.2],
  ['+/- 30%', 0.3],
  ['+/- 40%', 0.4]
];

const COMPS_BUILDING_SIZE_OPTIONS_FOR_SELECT = [
  ["Don't Apply", ''],
  ['+/- 10%', 0.1],
  ['+/- 20%', 0.2],
  ['+/- 30%', 0.3],
  ['+/- 40%', 0.4]
];

const COMPS_LOT_SIZE_OPTIONS_FOR_SELECT = [
  ["Don't Apply", ''],
  ['+/- 10%', 0.1],
  ['+/- 20%', 0.2],
  ['+/- 30%', 0.3],
  ['+/- 40%', 0.4]
];

const COMPS_SORT_OPTIONS_FOR_SELECT = [
  ['Default', ''],
  ['Last Sale Date', 'lastSaleDate:desc'],
  ['Last Sale Price', 'lastSalePrice:desc'],
  ['Distance', 'distance'],
  ['Lot Size SF', 'lotSizeSf:desc'],
  ['Sq Ft', 'buildingSizeSf:desc'],
  ['Year Built', 'yearBuilt:desc'],
  ['Year Renovated', 'yearRenovated:desc'],
  ['Units', 'numberOfUnits:desc']
];

const FAKE_PROPERTY_COMPS: InsightsProperty[] = [
  {
    address: '123 Main St.',
    city: 'Chicago',
    highlights: [],
    lastSaleDate: '',
    lastSalePrice: 123456,
    latitude: 0,
    listings: {
      forSale: true,
      salePrice: 123456,
      brokerInfo: [],
      forLease: false,
      originalListingDate: null,
      soldDate: null,
      soldPrice: 0
    },
    longitude: 0,
    propertyId: 'abc',
    propertyTypeId: 10,
    scores: {
      likelyToSell: {
        rank: 1
      },
      likelyToRefi: null
    },
    state: 'IL',
    streetviewUrl: 'https://maps.googleapis.com/maps/api/streetview?return_error_code=true&source=outdoor&size=226x226',
    propertyType: '',
    zip: '',
    lastUpdated: ''
  },
  {
    address: '127 Main St.',
    city: 'Chicago',
    highlights: [],
    lastSaleDate: '',
    lastSalePrice: 133456,
    latitude: 0,
    listings: {
      forSale: true,
      salePrice: 133456,
      brokerInfo: [],
      forLease: false,
      originalListingDate: null,
      soldDate: null,
      soldPrice: 0
    },
    longitude: 0,
    propertyId: '123',
    propertyTypeId: 10,
    scores: {
      likelyToSell: {
        rank: 1
      },
      likelyToRefi: null
    },
    state: 'IL',
    streetviewUrl: 'https://maps.googleapis.com/maps/api/streetview?return_error_code=true&source=outdoor&size=226x226',
    propertyType: '',
    zip: '',
    lastUpdated: ''
  },
  {
    address: '131 Main St.',
    city: 'Chicago',
    highlights: [],
    lastSaleDate: '',
    lastSalePrice: 113456,
    latitude: 0,
    listings: {
      forSale: true,
      salePrice: 113456,
      brokerInfo: [],
      forLease: false,
      originalListingDate: null,
      soldDate: null,
      soldPrice: 0
    },
    longitude: 0,
    propertyId: '456',
    propertyTypeId: 10,
    scores: {
      likelyToSell: {
        rank: 1
      },
      likelyToRefi: null
    },
    state: 'IL',
    streetviewUrl: 'https://maps.googleapis.com/maps/api/streetview?return_error_code=true&source=outdoor&size=226x226',
    propertyType: '',
    zip: '',
    lastUpdated: ''
  },
  {
    address: '133 Main St.',
    city: 'Chicago',
    highlights: [],
    lastSaleDate: '',
    lastSalePrice: 173456,
    latitude: 0,
    listings: {
      forSale: true,
      salePrice: 173456,
      brokerInfo: [],
      forLease: false,
      originalListingDate: null,
      soldDate: null,
      soldPrice: 0
    },
    longitude: 0,
    propertyId: '789',
    propertyTypeId: 10,
    scores: {
      likelyToSell: {
        rank: 1
      },
      likelyToRefi: null
    },
    state: 'IL',
    streetviewUrl: 'https://maps.googleapis.com/maps/api/streetview?return_error_code=true&source=outdoor&size=226x226',
    propertyType: '',
    zip: '',
    lastUpdated: ''
  }
];

export default function CompsSection({ fromTurbo = false, hasAccess, subjectProperty }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [distance, setDistance] = useState('1');
  const [timePeriod, setTimePeriod] = useState('12');
  const [priceRange, setPriceRange] = useState('');
  const [buildingSize, setBuildingSize] = useState('');
  const [lotSize, setLotSize] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [properties, setProperties] = useState<any[]>();
  const [hasFetched, setHasFetched] = useState(false);
  const insightsPropertyId = subjectProperty.propertyId;

  const handlePropertyRemoval = useCallback((propertyId) => {
    if (!properties) return;
    const updatedProperties = properties.filter(property => property.propertyId !== propertyId);
    setProperties(updatedProperties);
  }, [properties]);

  const handleUpdateComps = () => {
    if (window.gtag) {
      window.gtag('event', 'connect_off_market_property_comps_search', { propertyId: insightsPropertyId });
    }
    getComps(insightsPropertyId, {
      distance, timePeriod, priceRange, buildingSize, lotSize, sortBy
    }).then((res) => {
      setProperties(res);
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry], observerInstance) => {
        if (entry.isIntersecting && !hasFetched) {
          if (window.gtag) {
            window.gtag('event', 'connect_off_market_property_comps_search', { propertyId: insightsPropertyId });
          }
          getComps(insightsPropertyId, {
            distance, timePeriod, priceRange, buildingSize, lotSize, sortBy
          }).then((res) => {
            setProperties(res);
            setHasFetched(true);
            observerInstance.unobserve(entry.target);
          });
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasFetched, insightsPropertyId]);


  if (!hasAccess) {
    return (
      <>
      <div className="d-flex alignt-items-center gap-2 mb-3">
        <FontAwesomeIcon className="text-primary" icon={faCodeCompare} />
        <h6 className="my-0 fw-bolder">Comparables</h6>
      </div>
      <Row className="g-3">
        {FAKE_PROPERTY_COMPS.map(property => (
          <Col key={property.propertyId} md={6} xs={12}>
            <OffMarketDetailsCard property={property} />
          </Col>
        ))}
      </Row>
      </>
    );
  }

  if (!hasFetched) {
    return (
      <div className="text-center p-3" ref={containerRef}>
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-6">
      <div>
        <div className="d-flex align-items-center gap-2 mb-3">
          <FontAwesomeIcon className="text-primary" icon={faSlidersUp} />
          <h6 className="my-0 fw-bolder">Parameters</h6>
        </div>
        <div>
          <Row className="g-3 mb-3">
            <Col md={4} xs={12}>
              <Form.Label>Distance</Form.Label>
              <Form.Select
                value={distance}
                onChange={e => setDistance(e.target.value)}
              >
                {COMPS_DISTANCE_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} xs={12}>
              <Form.Label>Time Period</Form.Label>
              <Form.Select
                value={timePeriod}
                onChange={e => setTimePeriod(e.target.value)}
              >
                {COMPS_TIME_PERIOD_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} xs={12}>
              <Form.Label>Price Range</Form.Label>
              <Form.Select
                value={priceRange}
                onChange={e => setPriceRange(e.target.value)}
              >
                {COMPS_PRICE_RANGE_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} xs={12}>
              <Form.Label>Building Size</Form.Label>
              <Form.Select
                value={buildingSize}
                onChange={e => setBuildingSize(e.target.value)}
              >
                {COMPS_BUILDING_SIZE_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} xs={12}>
              <Form.Label>Lot Size</Form.Label>
              <Form.Select
                value={lotSize}
                onChange={e => setLotSize(e.target.value)}
              >
                {COMPS_LOT_SIZE_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} xs={12}>
              <Form.Label>Sort By</Form.Label>
              <Form.Select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                {COMPS_SORT_OPTIONS_FOR_SELECT.map(([label, val]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="justify-content-end">
            <Col md="auto" xs={12}>
              <Button className="w-100" label="Update Comps" onClick={handleUpdateComps} />
            </Col>
          </Row>
        </div>
      </div>
      {properties && properties.length == 0 && (
        <EmptyState
          icon={faBuildingMagnifyingGlass}
          iconSize="2x"
          text="Try expanding your search by changing the parameters" title="No Comps found"
        />
      )}
      {properties && properties.length > 0 && (
        <OffMarketComps
          fromTurbo={fromTurbo}
          properties={properties}
          subjectProperty={subjectProperty}
          onPropertyRemoval={handlePropertyRemoval}
        />
      )}
    </div>
  );
}
